import {
  AiChatHistory,
  CreateFeedCardMutation,
  CreateStepMutation,
  GetGiideAnalyticsQuery,
  GetGiidePaddleAccountDetailsQuery,
  GetGiidePaddleTransactionsQuery,
  GetGiideQuery,
  GetRecordingQuery,
  GetUserAudioLibraryQuery,
  TranscriptItemInput,
  TranscriptResultsInput,
} from './api';
import {
  CreateGiideErrorType,
  ExportVideoState,
  Filters,
  GiideAudioLibraryFetching,
  giideListPaginationPage,
  giideListSort,
  giideTabState,
  ImageEditorState,
  ImageEditorValidation,
  PaymentState,
  Plan,
  PreviewDetails,
  SettingsData,
} from '../types';
import { EditorState } from 'draft-js';
import { ModalContent } from '../hooks/useGiideContentModal';

export interface Subscription {
  closed: boolean;
  unsubscribe(): void;
}

export type GiideAnalytics = GetGiideAnalyticsQuery['getGiideAnalytics'] & {
  id: string;
  period: number;
  grouping: string;
};

export type PaddleAccountDetails =
  GetGiidePaddleAccountDetailsQuery['getGiidePaddleAccountDetails'];
export type PaddleTransactions =
  GetGiidePaddleTransactionsQuery['getGiidePaddleTransactions'];

export type Giide = GetGiideQuery['getGiide'] & {
  giideLibrarySpeaker?: Speaker;
  giideLibraryOwner?: string;
  giideLibraryTenantID?: string;
  giideLibraryDescription?: string;
  giideLibraryAlreadyMerged?: boolean;
  exportVideoState?: string;
};

export type Step = CreateStepMutation['createStep'] & {
  editing?: boolean;
  recording?: Recording;
  isInLibrary?: boolean;
};

export type Recording = GetRecordingQuery['getRecording'] & {
  transcriptResults: TranscriptResultsInput | null;
};

export type FeedCard = CreateFeedCardMutation['createFeedCard'] & {
  customImageBlob?: string;
  fetchedUrl?: string;
  headshotPath?: string;
  selectedWord?: string;
  isInLibrary?: boolean;
  stepIndex?: number;
  imageBlob?: string;
  uploadedImgBlob?: string;
  htmlText?: string | EditorState;
};

export interface AI {
  [giideId: string]: {
    conversationHistory: AiChatHistory[];
  };
}

export type SocialUrls = FeedCard['socialUrls'];

export interface IAdminUser {
  userId?: string;
  tenantID?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
}

export interface IUser {
  userId: string;
  tenantID: string;
  userRole: string;
  fullName: string;
  email?: string;
  title?: string;
  preferences?: {};
  profileImageName?: string;
  profileImageId?: string;
  profileImageService?: string;
  planId: string;
  plan: Plan;
}

export interface IFeedCard extends Partial<FeedCard> {
  style?: {};
  className?: string;
  onClick?: () => void;
}

export type PlayerState = {
  wasPlaying?: boolean;
  playing?: boolean;
  seekTo?: (amount: number, type?: 'seconds' | 'fraction') => void;
  progressTime?: number;
  length?: number;
  playingFrom?: number;
};

export type SubscriptionsState = {
  giideState: { [key: string]: Subscription | undefined };
  updateGiide?: Subscription;
  createGiide?: Subscription;
  deleteGiide?: Subscription;
  createRecording?: Subscription;
  updateRecording?: Subscription;
  updateStep?: Subscription;
  transcriptionFinished?: Subscription;
  updateAudioLibrary?: Subscription;
  createAudioLibrary?: Subscription;
  deleteAudioLibrary?: Subscription;
  updatePlan?: Subscription;
  exportVideo?: Subscription;
  imageUpload?: Subscription;
};

export interface IState {
  getStartedModalOpen: false | 'sample' | 'upload';
  graphicFormScrollPos: null | number;
  subscriptions: SubscriptionsState;
  highlightedCardId?: string | null;
  giideListFetching?: boolean;
  showButtons?: string;
  recordingProcessingModalVisible?: boolean;
  recordingProcessingModalShowed?: boolean;
  recordingErrorModalVisible?: boolean;
  newStepModal?: boolean;
  player?: PlayerState;
  user?: IUser;
  giides?: Giide[];
  avatar?: string;
  steps: Step[];
  giide?: Giide;
  cardProps?: Partial<FeedCard>;
  adminUsers?: IAdminUser[];
  tabState?: giideTabState;
  leftSideTabState?: 'notes' | 'feed';
  openedStepOrder?: number;
  giideListFilter?: Filters;
  giideListSearch?: string;
  recordingProcessingState?: {};
  tenantCategories?: ICategory[];
  libraryGiides?: Giide[];
  libraryAuthorAvatar?: string;
  isScraping?: boolean;
  isInitialScrapingDone?: boolean;
  relinkingFeedcard?: string | null;
  editingCardProps?: Partial<FeedCard>;
  audioLibrary?: UserAudioLibrary[];
  giideAudioLibraryFetching?: GiideAudioLibraryFetching;
  feedcardValidationFailed?: boolean;
  publishOpenedStep: number;
  giideListSortType?: giideListSort;
  giideListPaginationPage?: giideListPaginationPage;
  giideListViewType?: string;
  accountPage?: string;
  editingChapterId?: string;
  audioUploadProgress?: number;
  audioUploadFileName?: string;
  audioUploadFileId?: string;
  userFetching?: boolean;
  userLoggingIn?: boolean;
  previewDetails?: PreviewDetails;
  useTemplate?: boolean;
  oldGiideData?: SettingsData;
  newImgLoadedBlob?: string;
  imageEditorState?: ImageEditorState;
  publishFlowGiideBeforePreview?: SettingsData;
  imageEditorValidation?: ImageEditorValidation;
  activeWordInTranscribe?: TranscriptItemInput;
  addFeedcardModalOpened?: boolean;
  imageMimeTypeLoaded?: string;
  newFeedcardSaving?: boolean;
  ownRecordingStarted?: boolean;
  upgradeModalOpen: boolean;
  needToRefetchSubData: boolean;
  analyticsID?: string;
  analyticsData?: GiideAnalytics;
  paddleAccountDetails?: PaddleAccountDetails;
  paddleTransactions?: PaddleTransactions;
  paymentProcessing: boolean;
  transcriptData?: TranscriptItemInput[];
  accountDisabled: boolean;
  paymentState: PaymentState;
  exportVideoState: ExportVideoState;
  createGiideError: CreateGiideErrorType | null;
  imageEditorImage: ImageEditorImage | null;
  imageSubscriptions: Set<string>;
  imageChanged?: boolean;
  AI: AI;
  giideContentModal: ModalContent;
  giidePasswordModal: {
    giideId: string | null;
    modalType?: PasswordModalVariants;
  };
  unlockedGiides: string[];
  copyGiideModal: string | null;
}

export enum PasswordModalVariants {
  Create,
  Check,
  Delete,
}

export interface ImageEditorImage {
  objectUrl?: string;
  name?: string;
  type?: string;
  extension?: string;
  unsplashImage?: string;
  giphyImage?: string;
}

export interface IRecordingProcessingState {
  state?: 'processing' | 'finished';
  recordingId?: string;
  stepId?: string;
}

export interface ICategory {
  cardIds?: string[];
  cardSize?: string;
  createdAt: string;
  displayName: string;
  displayOrder: number;
  fistId: string;
  isActive: boolean;
  name: string;
  secondId: string;
  tenantID: string;
  updatedAt: string;
}

export type Speaker = {
  firstName: string;
  lastName: string;
  title?: string;
  bio?: string;
  profile?: string;
  headshotPath?: string;
  socialUrls?: SocialUrls;
};

export type GetUrlQueryType = {
  success: boolean;
  error: string | null;
  url: string | null;
  fields?: FormData | null;
};

export type UserAudioLibrary = GetUserAudioLibraryQuery['getUserAudioLibrary'];
